<script setup>
import EnableModuleAction from '@frontJs/components/actions/EnableModuleAction.vue';
import CheckCircleIcon from '@iconify-vue/ph/check-circle';
import CubeIcon from '@iconify-vue/ph/cube';
import GearIcon from '@iconify-vue/ph/gear';
import DisplayContentAction from '@js/components/actions/DisplayContentAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import LinkCell from '@js/components/tables/cells/LinkCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { toastSuccess } from '@js/utils/toast';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { type } = defineProps({
    type: {
        type: String,
        required: false,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    setGlobalFilter,
    availableFilters,
    filters,
    setFilters,
    globalData,
    selectionIds,
    updateData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'name',
            header: () => 'Nom',
            cell: (props) =>
                h(LinkCell, {
                    text: props.row.original.name,
                    href:
                        route(
                            'front.projectManagement.customer.updateCustomer',
                            {
                                tenant: props.row.original.id,
                            },
                        ) + '?tab=modules',
                    target: '_self',
                    inertia: true,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'enabledModuleCount',
            header: () => 'Nb modules activés',
            cell: (props) =>
                h(TextCell, {
                    text:
                        props.row.original.enabledModuleCount +
                        '/' +
                        props.row.original.modules.length,
                    displayAsNumber: true,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'enabledExternalServiceCount',
            header: () => 'Nb services externes activés',
            cell: (props) =>
                h(TextCell, {
                    text:
                        props.row.original.enabledExternalServiceCount +
                        '/' +
                        props.row.original.externalServices.length,
                    displayAsNumber: true,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'userCount',
            header: () => 'NB utilisateurs',
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.userCount,
                    color:
                        props.row.original.userCount === 0 ? 'red' : undefined,
                }),
        }),
        columnHelper.display({
            id: 'details',
            header: 'Détails',
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(
                                DisplayContentAction,
                                {
                                    title: 'Modules',
                                    content: h('ul', {}, [
                                        props.row.original.modules.map(
                                            (module) =>
                                                h(
                                                    'li',
                                                    {
                                                        class: module.active
                                                            ? 'text-green-500'
                                                            : 'text-red-500',
                                                    },
                                                    module.fullTitle +
                                                        (module.typeLabel
                                                            ? ' - ' +
                                                              module.typeLabel
                                                            : ''),
                                                ),
                                        ),
                                    ]),
                                },
                                {
                                    trigger: () =>
                                        h(ActionButton, {
                                            size: 'small',
                                            square: true,
                                            title: 'Voir les modules',
                                            type: 'primary',
                                            icon: CubeIcon,
                                        }),
                                },
                            ),
                            h(
                                DisplayContentAction,
                                {
                                    title: 'Services externes',
                                    content: h('ul', {}, [
                                        props.row.original.externalServices.map(
                                            (externalService) =>
                                                h(
                                                    'li',
                                                    {
                                                        class: externalService.active
                                                            ? 'text-green-500'
                                                            : 'text-red-500',
                                                    },
                                                    externalService.title,
                                                ),
                                        ),
                                    ]),
                                },
                                {
                                    trigger: () =>
                                        h(ActionButton, {
                                            size: 'small',
                                            square: true,
                                            title: 'Voir les services externes',
                                            type: 'primary',
                                            icon: GearIcon,
                                        }),
                                },
                            ),
                        ],
                    },
                ),
        }),
    ],
    url: route('front.postModules', { tenantReport: true }),
    data: {
        type,
    },
    columnPinning: {
        right: ['actions'],
    },
    selectionIdKey: 'id',
});

function enableCustomerModuleActionCallback() {
    toastSuccess('Activé avec succès.');

    updateData();
}
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :set-global-filter="setGlobalFilter"
        :available-filters="availableFilters"
        :filters="filters"
        :set-filters="setFilters"
        :loading="loading"
    >
        <template #actions>
            <EnableModuleAction
                :modules="globalData.modules"
                :tenant-ids="selectionIds"
                :callback="enableCustomerModuleActionCallback"
            >
                <template #trigger>
                    <ActionButton
                        size="small"
                        :left-icon="CheckCircleIcon"
                        :disabled="selectionIds.length === 0"
                        type="primary"
                    >
                        Activer un module
                    </ActionButton>
                </template>
            </EnableModuleAction>
        </template>
    </DataTableLayout>
</template>
